/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantsAPI } from '../../services/api';
import { effectiveJurisdiction } from '../../utils/propertyCountry';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import {
  fmtDate, fmtRent, currencySymbol, leaseStatus,
  TENANCY_TYPES, DEPOSIT_SCHEMES, CHECKLIST_ITEMS,
  getIrishComplianceIssues, PROPERTY_TYPE_LABELS
} from './tenantConstants';

// ── Inline edit section wrapper ───────────────────────────────────────────────
function Section({ title, icon, children, onEdit, editing, onSave, onCancel, saving }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing ? '1rem' : '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {editing ? (
            <>
              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }} onClick={onCancel} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }} onClick={onSave} disabled={saving}>{saving ? 'Saving…' : '✅ Save'}</button>
            </>
          ) : (
            <button className="btn btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }} onClick={onEdit}>✏️ Edit</button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Read-only field ───────────────────────────────────────────────────────────
function Field({ label, value, wide }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>{label}</div>
      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{value || '—'}</div>
    </div>
  );
}

// ── Edit field ────────────────────────────────────────────────────────────────
function EditField({ label, type = 'text', value, onChange, options, wide, small }) {
  return (
    <div className="form-group" style={{ gridColumn: wide ? '1 / -1' : undefined, marginBottom: '0.75rem' }}>
      <label style={{ fontSize: '0.75rem' }}>{label}</label>
      {options ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ fontSize: '0.875rem' }}>
          <option value="">Select…</option>
          {options.map(o => typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
          )}
        </select>
      ) : (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} style={{ fontSize: '0.875rem' }} />
      )}
      {small && <small style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>{small}</small>}
    </div>
  );
}

const GRID2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' };
const GRID3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' };

// ── Main Component ────────────────────────────────────────────────────────────
export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, setTenants } = useAppData();
  const { isAdmin } = useAuth();

  const [tenant, setTenant]           = useState(null);
  const [jointTenants, setJointTenants] = useState([]);
  const [payments, setPayments]       = useState([]);
  const [comms, setComms]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [activeTab, setActiveTab]     = useState('overview');

  // Editing state per section
  const [editingSection, setEditingSection] = useState(null);
  const [editForm, setEditForm]             = useState({});
  const [saving, setSaving]                 = useState(false);

  // TENANT-ERASURE-001: GDPR Article 17. requireAdmin on the server, so
  // gated to isAdmin() here too — a member should never even see the
  // option, not just have it fail if attempted.
  const [showEraseModal, setShowEraseModal] = useState(false);
  // BUG FIX: these four were originally declared further down, AFTER this
  // component's early returns (if (loading) return ...; if (error ||
  // !tenant) return ...). React requires the exact same hooks, in the exact
  // same order, on every render — the first render (while loading) hit the
  // early return before ever reaching them, so fewer hooks were called;
  // once data loaded and the component re-rendered past those guards, it
  // suddenly called MORE hooks than the first render did. That mismatch is
  // React error #310, and it crashed the page on every load once data
  // finished fetching — not something specific to editing, which is why it
  // affected every section. Hooks must always sit before any conditional
  // return, alongside the rest of the component's top-level state, exactly
  // where showEraseModal above already correctly does.
  const [showMoveOutModal, setShowMoveOutModal] = useState(false);
  const [moveOutDateInput, setMoveOutDateInput] = useState('');
  const [movingOut, setMovingOut] = useState(false);
  const [moveOutError, setMoveOutError] = useState('');
  const [eraseReason, setEraseReason]       = useState('');
  const [erasing, setErasing]               = useState(false);
  const [eraseError, setEraseError]         = useState('');

  // Joint tenant add
  const [showAddJoint, setShowAddJoint] = useState(false);
  const [jointForm, setJointForm]       = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [addingJoint, setAddingJoint]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const t = await tenantsAPI.getOne(id);
      setTenant(t);
      // Load joint tenants if non-HMO
      if (t.tenancy_ref && t.property_type !== 'hmo') {
        const all = await tenantsAPI.getByTenancyRef(t.tenancy_ref);
        setJointTenants(all.filter(j => j.id !== t.id && j.status === 'active'));
      }
      // Load payments and comms
      const [pmts, cms] = await Promise.all([
        tenantsAPI.getPayments(id),
        tenantsAPI.getCommunications(id),
      ]);
      setPayments(pmts);
      setComms(cms);
    } catch (e) {
      setError('Could not load tenant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading tenant…</div>;
  if (error || !tenant)   return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <p style={{ color: 'var(--danger)' }}>{error || 'Tenant not found'}</p>
      <button className="btn btn-secondary" onClick={() => navigate('/tenants')}>← Back to Tenants</button>
    </div>
  );

  const currency      = tenant.currency || 'GBP';
  const jurisdiction  = effectiveJurisdiction(tenant);
  const isIreland     = jurisdiction === 'ireland';
  const isHMO         = tenant.property_type === 'hmo';
  const isAgency      = tenant.management_type === 'agency';
  const sym           = currencySymbol(currency);
  const ls            = leaseStatus(tenant);
  const compIssues    = getIrishComplianceIssues({ ...tenant, jurisdiction });
  const tenancyTypes  = TENANCY_TYPES[jurisdiction] || TENANCY_TYPES.england;
  const depositSchemes = DEPOSIT_SCHEMES[jurisdiction] || DEPOSIT_SCHEMES.england;
  const checklistItems = CHECKLIST_ITEMS[jurisdiction] || CHECKLIST_ITEMS.england;
  const property      = properties.find(p => String(p.id) === String(tenant.property_id));
  const roomName      = isHMO ? (property?.rooms?.find(r => String(r.id) === String(tenant.room_id))?.roomName || tenant.room_id) : null;

  // ── Inline edit helpers ──────────────────────────────────────────────────
  const startEdit = (section, fields) => {
    setEditingSection(section);
    setEditForm({ ...fields });
  };

  const cancelEdit = () => { setEditingSection(null); setEditForm({}); };

  const saveSection = async () => {
    setSaving(true);
    try {
      const updated = await tenantsAPI.update(tenant.id, { ...tenant, ...editForm });
      // Preserve joined fields from the GET query that aren't returned by PUT
      const merged = {
        ...updated,
        jurisdiction:      updated.jurisdiction      || tenant.jurisdiction,
        currency:          updated.currency          || tenant.currency,
        property_address:  updated.property_address  || tenant.property_address,
        property_type:     updated.property_type     || tenant.property_type,
        management_type:   updated.management_type   || tenant.management_type,
        agency_name:       updated.agency_name       || tenant.agency_name,
      };
      setTenant(merged);
      setTenants(prev => prev.map(t => t.id === merged.id ? { ...t, ...merged } : t));
      setEditingSection(null);
      setEditForm({});
    } catch (e) {
      alert('Failed to save — please try again');
    } finally { setSaving(false); }
  };

  const ef = (field) => ({
    value: editForm[field] !== undefined ? editForm[field] : '',
    onChange: (v) => setEditForm(p => ({ ...p, [field]: v }))
  });

  // BUG FIX: this previously just confirmed and always recorded TODAY as
  // the move-out date, with no way to say otherwise. A landlord (or their
  // agent) recording a move-out days or weeks after it actually happened —
  // a common, realistic scenario — had no way to reflect the true
  // departure date. Since arrears/overdue and payment-attribution logic
  // now correctly use move_out_date as a hard boundary, an inaccurate date
  // here meant rent kept appearing owed for a period after the tenant had
  // genuinely left, and once someone new moved into the room, that phantom
  // arrears got inherited by them in the overdue calculations.
  const todayISO = () => new Date().toISOString().substring(0,10);

  const handleMoveOut = async () => {
    setMoveOutError('');
    if (moveOutDateInput > todayISO()) { setMoveOutError('Move-out date cannot be in the future.'); return; }
    const wasAlreadyMovedOut = tenant.status === 'moved_out';
    setMovingOut(true);
    try {
      await tenantsAPI.moveOut(tenant.id, moveOutDateInput || undefined);
      setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, status: 'moved_out', move_out_date: moveOutDateInput || todayISO() } : t));
      setShowMoveOutModal(false);
      // Only navigate away for an initial move-out — a date correction on
      // an already-moved-out tenant should stay put so the corrected date
      // is visible, rather than redirecting away right after fixing it.
      if (!wasAlreadyMovedOut) navigate('/tenants');
    } catch (e) { setMoveOutError(e.error || 'Failed to update tenant'); }
    finally { setMovingOut(false); }
  };

  // TENANT-ERASURE-001: anonymises identity fields in place — payment
  // history, lease dates and property attribution are retained (they have
  // their own basis under tax law). No undo: matches the nature of a GDPR
  // erasure request, and the server has no reversal endpoint either.
  const handleErase = async () => {
    setEraseError('');
    setErasing(true);
    try {
      const result = await tenantsAPI.erase(tenant.id, eraseReason);
      setTenant(result.tenant);
      setTenants(prev => prev.map(t => t.id === tenant.id ? result.tenant : t));
      setShowEraseModal(false);
      setEraseReason('');
    } catch (e) {
      setEraseError(e.error || 'Failed to erase tenant data.');
    } finally {
      setErasing(false);
    }
  };

  const handleAddJointTenant = async (e) => {
    e.preventDefault();
    if (!jointForm.first_name.trim()) return;
    setAddingJoint(true);
    try {
      const newTenant = await tenantsAPI.create({
        ...jointForm,
        property_id: tenant.property_id,
        room_id: tenant.room_id || null,
        tenancy_ref: tenant.tenancy_ref,
        is_lead_tenant: false,
        lease_start: tenant.lease_start,
        lease_end: tenant.lease_end,
        rent_amount: null, // joint tenant — rent on lead record
        tenancy_type: tenant.tenancy_type,
        status: 'active',
        email_reminders: false, // receipts go to lead by default
        email_receipts: false,
      });
      setJointTenants(prev => [...prev, newTenant]);
      setTenants(prev => [...prev, newTenant]);
      setJointForm({ first_name: '', last_name: '', email: '', phone: '' });
      setShowAddJoint(false);
    } catch (e) { alert('Failed to add joint tenant'); }
    finally { setAddingJoint(false); }
  };

  const checklistDone = checklistItems.filter(i => (tenant.checklist || {})[i.key]).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <button className="btn btn-secondary" style={{ marginBottom: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0.6rem' }} onClick={() => navigate('/tenants')}>
            ← Back to Tenants
          </button>
          <h1 style={{ margin: '0', fontSize: '1.5rem', fontWeight: 700 }}>
            {tenant.first_name} {tenant.last_name}
            {tenant.is_lead_tenant && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: 'var(--primary)', color: '#fff', borderRadius: '0.25rem', padding: '0.15rem 0.4rem', verticalAlign: 'middle' }}>Lead</span>}
            {tenant.status === 'moved_out' && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: 'var(--bg-light)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.15rem 0.4rem', color: 'var(--text-secondary)', verticalAlign: 'middle' }}>Moved Out</span>}
            {/* TENANT-ERASURE-001: distinct from "Moved Out" — this tenant's
                identity has been anonymised at their request, not just that
                the tenancy ended. Shown even though first_name already
                reads "Tenant A" etc, so it isn't mistaken for someone
                genuinely named that. */}
            {tenant.erased_at && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.25rem', padding: '0.15rem 0.4rem', color: '#92400e', verticalAlign: 'middle' }}>🔒 Erased — GDPR request</span>}
          </h1>
          <div style={{ marginTop: '0.35rem', fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>🏠 {tenant.property_address}</span>
            {roomName && <span>· Room: {roomName}</span>}
            <span>· {PROPERTY_TYPE_LABELS[tenant.property_type] || tenant.property_type}</span>
            {isAgency && <span style={{ color: 'var(--primary)' }}>· 🏢 {tenant.agency_name || 'Agency'}</span>}
            <span style={{ color: ls.color, fontWeight: 600 }}>· {ls.label}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {tenant.status === 'active' && (
            <button className="btn btn-secondary" style={{ color: 'var(--danger)', fontSize: '0.875rem' }} onClick={() => { setMoveOutDateInput(todayISO()); setMoveOutError(''); setShowMoveOutModal(true); }}>
              🚪 Move Out
            </button>
          )}
          {/* Correcting an already-recorded move-out date — a real, normal
              need (human error, or recorded after the fact and mis-dated),
              not just a one-off fix for today's bug. Reuses the exact same
              modal and the same backend call: DELETE /api/tenants/:id is
              safe to call again on an already moved_out tenant — it doesn't
              require status='active' first, and just updates move_out_date
              (status stays 'moved_out', a no-op re-write). Pre-fills with
              the CURRENT recorded date rather than today, since the point
              here is correcting it, not re-confirming "today". */}
          {tenant.status === 'moved_out' && !tenant.erased_at && (
            <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }} onClick={() => { setMoveOutDateInput((tenant.move_out_date || todayISO()).substring(0,10)); setMoveOutError(''); setShowMoveOutModal(true); }}>
              ✏️ Edit Move-Out Date
            </button>
          )}
          {/* TENANT-ERASURE-001: requireAdmin server-side, so gated to
              isAdmin() here too. Hidden once already erased — there is no
              undo, so re-showing the button would just invite a confusing
              "already erased" error rather than a clean disabled state. */}
          {isAdmin() && !tenant.erased_at && (
            <button className="btn btn-secondary" style={{ color: '#b45309', fontSize: '0.875rem' }} onClick={() => { setShowEraseModal(true); setEraseReason(''); setEraseError(''); }}>
              🔒 Erase Data (GDPR)
            </button>
          )}
        </div>
      </div>

      {/* ── Compliance alerts ── */}
      {compIssues.length > 0 && (
        <div style={{ marginBottom: '1.5rem', display: 'grid', gap: '0.5rem' }}>
          {compIssues.map(issue => (
            <div key={issue.code} style={{
              background: issue.severity === 'danger' ? '#fee2e2' : '#fef3c7',
              border: `1px solid ${issue.severity === 'danger' ? '#fca5a5' : '#fcd34d'}`,
              borderRadius: '0.5rem', padding: '0.75rem 1rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <span style={{ fontWeight: 700, color: issue.severity === 'danger' ? '#dc2626' : '#92400e' }}>
                  🇮🇪 {issue.code}: {issue.label}
                </span>
                <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: issue.severity === 'danger' ? '#dc2626' : '#92400e' }}>
                  {issue.detail}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="tabs" style={{ marginBottom: '1.5rem' }}>
        {[
          ['overview',    '👤 Overview'],
          ['lease',       '📋 Lease & Compliance'],
          ['deposit',     '🔒 Deposit & Legal'],
          ['payments',    '💰 Payments'],
          ['comms',       '✉️ Communications'],
        ].map(([val, label]) => (
          <button key={val} className={`tab-btn ${activeTab === val ? 'active' : ''}`}
            onClick={() => setActiveTab(val)}>{label}</button>
        ))}
      </div>

      {/* ══ OVERVIEW TAB ══════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div>
          {/* Personal details */}
          <Section title="Personal Details" icon="👤"
            editing={editingSection === 'personal'}
            onEdit={() => startEdit('personal', {
              first_name: tenant.first_name, last_name: tenant.last_name,
              email: tenant.email, phone: tenant.phone, date_of_birth: tenant.date_of_birth?.substring(0,10) || '',
              notes: tenant.notes,
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {editingSection === 'personal' ? (
              <div style={GRID2}>
                <EditField label="First Name *" {...ef('first_name')} />
                <EditField label="Last Name"    {...ef('last_name')} />
                <EditField label="Email" type="email" {...ef('email')} />
                <EditField label="Phone"        {...ef('phone')} />
                <EditField label="Date of Birth" type="date" {...ef('date_of_birth')} />
                <EditField label="Notes" {...ef('notes')} wide />
              </div>
            ) : (
              <div style={GRID2}>
                <Field label="First Name"   value={tenant.first_name} />
                <Field label="Last Name"    value={tenant.last_name} />
                <Field label="Email"        value={tenant.email} />
                <Field label="Phone"        value={tenant.phone} />
                <Field label="Date of Birth" value={fmtDate(tenant.date_of_birth)} />
                {tenant.notes && <Field label="Notes" value={tenant.notes} wide />}
              </div>
            )}
          </Section>

          {/* Emergency contact */}
          <Section title="Emergency Contact" icon="🚨"
            editing={editingSection === 'emergency'}
            onEdit={() => startEdit('emergency', {
              emergency_name: tenant.emergency_name, emergency_phone: tenant.emergency_phone,
              emergency_relationship: tenant.emergency_relationship,
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {editingSection === 'emergency' ? (
              <div style={GRID3}>
                <EditField label="Name"         {...ef('emergency_name')} />
                <EditField label="Phone"        {...ef('emergency_phone')} />
                <EditField label="Relationship" {...ef('emergency_relationship')} />
              </div>
            ) : (
              <div style={GRID3}>
                <Field label="Name"         value={tenant.emergency_name} />
                <Field label="Phone"        value={tenant.emergency_phone} />
                <Field label="Relationship" value={tenant.emergency_relationship} />
              </div>
            )}
          </Section>

          {/* Guarantor */}
          <Section title="Guarantor" icon="🤝"
            editing={editingSection === 'guarantor'}
            onEdit={() => startEdit('guarantor', {
              guarantor_name: tenant.guarantor_name, guarantor_email: tenant.guarantor_email,
              guarantor_phone: tenant.guarantor_phone,
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {editingSection === 'guarantor' ? (
              <div style={GRID3}>
                <EditField label="Name"  {...ef('guarantor_name')} />
                <EditField label="Email" type="email" {...ef('guarantor_email')} />
                <EditField label="Phone" {...ef('guarantor_phone')} />
              </div>
            ) : (
              <div style={GRID3}>
                <Field label="Name"  value={tenant.guarantor_name} />
                <Field label="Email" value={tenant.guarantor_email} />
                <Field label="Phone" value={tenant.guarantor_phone} />
              </div>
            )}
          </Section>

          {/* Joint tenants — non-HMO only */}
          {!isHMO && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>👥</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Joint Tenants</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(same tenancy — one rent payment)</span>
                </div>
                <button className="btn btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                  onClick={() => setShowAddJoint(true)}>+ Add Joint Tenant</button>
              </div>
              {jointTenants.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No joint tenants on this tenancy.</p>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {jointTenants.map(jt => (
                    <div key={jt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--bg-light)', borderRadius: '0.375rem' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{jt.first_name} {jt.last_name}</span>
                        {jt.email && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{jt.email}</span>}
                      </div>
                      <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => navigate(`/tenants/${jt.id}`)}>View →</button>
                    </div>
                  ))}
                </div>
              )}
              {/* Add joint tenant form */}
              {showAddJoint && (
                <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem', border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.875rem' }}>Add Joint Tenant</div>
                  <form onSubmit={handleAddJointTenant}>
                    <div style={GRID2}>
                      <div className="form-group"><label>First Name *</label><input value={jointForm.first_name} onChange={e => setJointForm(p => ({...p, first_name: e.target.value}))} required /></div>
                      <div className="form-group"><label>Last Name</label><input value={jointForm.last_name} onChange={e => setJointForm(p => ({...p, last_name: e.target.value}))} /></div>
                      <div className="form-group"><label>Email</label><input type="email" value={jointForm.email} onChange={e => setJointForm(p => ({...p, email: e.target.value}))} /></div>
                      <div className="form-group"><label>Phone</label><input value={jointForm.phone} onChange={e => setJointForm(p => ({...p, phone: e.target.value}))} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowAddJoint(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={addingJoint}>{addingJoint ? 'Adding…' : '+ Add'}</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* Communications preferences */}
          <Section title="Communication Preferences" icon="✉️"
            editing={editingSection === 'comms_prefs'}
            onEdit={() => startEdit('comms_prefs', {
              email_reminders: tenant.email_reminders,
              email_receipts: tenant.email_receipts,
              comms_override: tenant.comms_override,
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {isAgency && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#1e40af' }}>
                🏢 Property managed by agency — communications off by default
              </div>
            )}
            {editingSection === 'comms_prefs' ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {isAgency && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                    <input type="checkbox" checked={!!editForm.comms_override} onChange={e => setEditForm(p => ({...p, comms_override: e.target.checked}))} />
                    Override agency — send emails directly to this tenant
                  </label>
                )}
                {(!isAgency || editForm.comms_override) && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={!!editForm.email_reminders} onChange={e => setEditForm(p => ({...p, email_reminders: e.target.checked}))} />
                      Send rent reminders
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={!!editForm.email_receipts} onChange={e => setEditForm(p => ({...p, email_receipts: e.target.checked}))} />
                      Send rent receipts
                    </label>
                  </>
                )}
              </div>
            ) : (
              <div style={GRID3}>
                <Field label="Rent Reminders" value={isAgency && !tenant.comms_override ? '🔕 Agency managed' : tenant.email_reminders ? '🔔 Enabled' : '🔕 Disabled'} />
                <Field label="Rent Receipts"  value={isAgency && !tenant.comms_override ? '🔕 Agency managed' : tenant.email_receipts  ? '🧾 Enabled' : '🔕 Disabled'} />
                {isAgency && <Field label="Comms Override" value={tenant.comms_override ? '✅ Overridden' : '—'} />}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* ══ LEASE & COMPLIANCE TAB ════════════════════════════════════════ */}
      {activeTab === 'lease' && (
        <div>
          <Section title="Tenancy Details" icon="📋"
            editing={editingSection === 'lease'}
            onEdit={() => startEdit('lease', {
              tenancy_type: tenant.tenancy_type,
              lease_start: tenant.lease_start?.substring(0,10) || '',
              lease_end: tenant.lease_end?.substring(0,10) || '',
              rent_amount: tenant.rent_amount || '',
              payment_due_day: tenant.payment_due_day || 1,
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {editingSection === 'lease' ? (
              <div style={GRID2}>
                <EditField label="Tenancy Type" options={tenancyTypes} {...ef('tenancy_type')} />
                <EditField label="Lease Start" type="date" {...ef('lease_start')} />
                <EditField label="Lease End" type="date" {...ef('lease_end')} small="Leave blank for periodic / rolling tenancy" />
                <EditField label={`Monthly Rent (${sym})`} type="number" {...ef('rent_amount')} />
                <EditField label="Payment Due Day" options={Array.from({length:28},(_,i)=>({value:String(i+1),label:`Day ${i+1}`}))} {...ef('payment_due_day')} />
              </div>
            ) : (
              <div style={GRID2}>
                <Field label="Tenancy Type"    value={tenancyTypes.find(t => t.value === tenant.tenancy_type)?.label || tenant.tenancy_type} />
                <Field label="Lease Start"     value={fmtDate(tenant.lease_start)} />
                <Field label="Lease End"       value={tenant.lease_end ? fmtDate(tenant.lease_end) : 'Periodic / Rolling'} />
                <Field label="Monthly Rent"    value={fmtRent(tenant.rent_amount, currency)} />
                <Field label="Payment Due Day" value={tenant.payment_due_day ? `Day ${tenant.payment_due_day}` : '—'} />
                <Field label="Lease Status"    value={<span style={{ color: ls.color, fontWeight: 600 }}>{ls.label}</span>} />
              </div>
            )}
          </Section>

          {/* Right to Rent — non-Ireland */}
          {!isIreland && (
            <Section title="Right to Rent" icon="🪪"
              editing={editingSection === 'rtr'}
              onEdit={() => startEdit('rtr', {
                right_to_rent_checked: tenant.right_to_rent_checked,
                right_to_rent_date: tenant.right_to_rent_date?.substring(0,10) || '',
                right_to_rent_doc: tenant.right_to_rent_doc || '',
                right_to_rent_expiry: tenant.right_to_rent_expiry?.substring(0,10) || '',
              })}
              onSave={saveSection} onCancel={cancelEdit} saving={saving}>
              {editingSection === 'rtr' ? (
                <div style={GRID2}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={!!editForm.right_to_rent_checked} onChange={e => setEditForm(p => ({...p, right_to_rent_checked: e.target.checked}))} />
                      Right to Rent check completed
                    </label>
                  </div>
                  <EditField label="Document Type" {...ef('right_to_rent_doc')} />
                  <EditField label="Check Date"    type="date" {...ef('right_to_rent_date')} />
                  <EditField label="Document Expiry" type="date" {...ef('right_to_rent_expiry')} />
                </div>
              ) : (
                <div style={GRID2}>
                  <Field label="Check Completed" value={tenant.right_to_rent_checked ? '✅ Yes' : '❌ No'} />
                  <Field label="Document Type"   value={tenant.right_to_rent_doc} />
                  <Field label="Check Date"      value={fmtDate(tenant.right_to_rent_date)} />
                  <Field label="Expiry Date"     value={fmtDate(tenant.right_to_rent_expiry)} />
                </div>
              )}
            </Section>
          )}

          {/* RTB & RPZ — Ireland */}
          {isIreland && (
            <Section title="RTB Registration" icon="🇮🇪"
              editing={editingSection === 'rtb'}
              onEdit={() => startEdit('rtb', {
                rtb_registered: tenant.rtb_registered,
                rtb_registration_number: tenant.rtb_registration_number || '',
                rtb_registration_date: tenant.rtb_registration_date?.substring(0,10) || '',
                rpz_property: tenant.rpz_property,
                last_rent_review_date: tenant.last_rent_review_date?.substring(0,10) || '',
              })}
              onSave={saveSection} onCancel={cancelEdit} saving={saving}>
              {editingSection === 'rtb' ? (
                <div style={GRID2}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={!!editForm.rtb_registered} onChange={e => setEditForm(p => ({...p, rtb_registered: e.target.checked}))} />
                      RTB Registration completed
                    </label>
                  </div>
                  <EditField label="RTB Reference Number" {...ef('rtb_registration_number')} />
                  <EditField label="RTB Registration Date" type="date" {...ef('rtb_registration_date')} />
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={!!editForm.rpz_property} onChange={e => setEditForm(p => ({...p, rpz_property: e.target.checked}))} />
                      Property is in a Rent Pressure Zone (RPZ)
                    </label>
                  </div>
                  {editForm.rpz_property && <EditField label="Last Rent Review Date" type="date" {...ef('last_rent_review_date')} />}
                </div>
              ) : (
                <div style={GRID2}>
                  <Field label="RTB Registered"        value={tenant.rtb_registered ? '✅ Yes' : '❌ No'} />
                  <Field label="RTB Reference"          value={tenant.rtb_registration_number} />
                  <Field label="Registration Date"      value={fmtDate(tenant.rtb_registration_date)} />
                  <Field label="RPZ Property"           value={tenant.rpz_property ? '✅ Yes' : 'No'} />
                  {tenant.rpz_property && <Field label="Last Rent Review" value={fmtDate(tenant.last_rent_review_date)} />}
                </div>
              )}
            </Section>
          )}

          {/* Move-in Checklist */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>✅</span>
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Move-in Checklist</span>
              </div>
              <span style={{ fontSize: '0.8rem', color: checklistDone === checklistItems.length ? 'var(--success)' : 'var(--warning)', fontWeight: 600 }}>
                {checklistDone}/{checklistItems.length} complete
              </span>
            </div>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {checklistItems.map(item => {
                const checked = !!(tenant.checklist || {})[item.key];
                return (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                    <input type="checkbox" checked={checked} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
                      onChange={async () => {
                        const updated = { ...tenant, checklist: { ...(tenant.checklist||{}), [item.key]: !checked } };
                        try {
                          const result = await tenantsAPI.update(tenant.id, updated);
                          const merged = {
                            ...result,
                            jurisdiction:     result.jurisdiction     || tenant.jurisdiction,
                            currency:         result.currency         || tenant.currency,
                            property_address: result.property_address || tenant.property_address,
                            property_type:    result.property_type    || tenant.property_type,
                            management_type:  result.management_type  || tenant.management_type,
                            agency_name:      result.agency_name      || tenant.agency_name,
                          };
                          setTenant(merged);
                          setTenants(prev => prev.map(t => t.id === merged.id ? {...t, ...merged} : t));
                        } catch (e) { alert('Failed to update checklist'); }
                      }}
                    />
                    <span style={{ color: checked ? 'var(--text-secondary)' : 'var(--text-primary)', textDecoration: checked ? 'line-through' : 'none' }}>
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══ DEPOSIT & LEGAL TAB ═══════════════════════════════════════════ */}
      {activeTab === 'deposit' && (
        <div>
          <Section title="Deposit Details" icon="🔒"
            editing={editingSection === 'deposit'}
            onEdit={() => startEdit('deposit', {
              deposit_amount: tenant.deposit_amount || '',
              deposit_scheme: tenant.deposit_scheme || '',
              deposit_reference: tenant.deposit_reference || '',
              deposit_paid_date: tenant.deposit_paid_date?.substring(0,10) || '',
              deposit_protected_date: tenant.deposit_protected_date?.substring(0,10) || '',
            })}
            onSave={saveSection} onCancel={cancelEdit} saving={saving}>
            {editingSection === 'deposit' ? (
              <div style={GRID2}>
                <EditField label={`Deposit Amount (${sym})`} type="number" step="0.01" {...ef('deposit_amount')} />
                <EditField label="Protection Scheme" options={depositSchemes} {...ef('deposit_scheme')} />
                <EditField label="Scheme Reference"  {...ef('deposit_reference')} />
                <EditField label="Deposit Paid Date" type="date" {...ef('deposit_paid_date')} />
                <EditField label="Protected Date"    type="date" {...ef('deposit_protected_date')} />
              </div>
            ) : (
              <div style={GRID2}>
                <Field label="Deposit Amount"    value={fmtRent(tenant.deposit_amount, currency)} />
                <Field label="Protection Scheme" value={tenant.deposit_scheme} />
                <Field label="Scheme Reference"  value={tenant.deposit_reference} />
                <Field label="Deposit Paid"      value={fmtDate(tenant.deposit_paid_date)} />
                <Field label="Protected Date"    value={fmtDate(tenant.deposit_protected_date)} />
              </div>
            )}
          </Section>

          {/* RPZ Calculator — Ireland */}
          {isIreland && tenant.rpz_property && tenant.rent_amount && (
            <div className="card" style={{ marginBottom: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span>📊</span>
                <span style={{ fontWeight: 700, color: '#166534', fontSize: '0.9rem' }}>RPZ Rent Increase Calculator</span>
              </div>
              <div style={GRID2}>
                <Field label="Current Rent"        value={`€${parseFloat(tenant.rent_amount).toFixed(2)}/month`} />
                <Field label="Max Increase (2% RPZ cap)" value={<span style={{ color: '#166534', fontWeight: 700 }}>€{(parseFloat(tenant.rent_amount) * 0.02).toFixed(2)}/month</span>} />
                <Field label="Maximum New Rent"    value={<span style={{ color: '#166534', fontWeight: 700, fontSize: '1.1rem' }}>€{(parseFloat(tenant.rent_amount) * 1.02).toFixed(2)}/month</span>} />
                <Field label="Last Review Date"    value={tenant.last_rent_review_date ? fmtDate(tenant.last_rent_review_date) : 'Not set'} />
              </div>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                RPZ cap is 2% or CPI (whichever is lower). Verify current CPI at{' '}
                <a href="https://www.cso.ie" target="_blank" rel="noreferrer" style={{ color: '#166534' }}>cso.ie</a>{' '}
                before proceeding. 90 days written notice required.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ══ PAYMENTS TAB ══════════════════════════════════════════════════ */}
      {activeTab === 'payments' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '0.9rem' }}>💰 Payment History</div>
          {payments.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No payments recorded yet.</p>
          ) : (
            <>
              <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Total paid: <strong style={{ color: 'var(--text-primary)' }}>{sym}{payments.reduce((s, p) => s + parseFloat(p.amount||0), 0).toFixed(2)}</strong>
                {' '}across <strong>{payments.length}</strong> payment{payments.length !== 1 ? 's' : ''}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-light)', borderBottom: '1px solid var(--border)' }}>
                      {['Month', 'Amount', 'Date Paid'].map(h => (
                        <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-light)' }}>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{p.month}</td>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{sym}{parseFloat(p.amount||0).toFixed(2)}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>{fmtDate(p.paid_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ COMMUNICATIONS TAB ════════════════════════════════════════════ */}
      {activeTab === 'comms' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '0.9rem' }}>✉️ Communication Log</div>
          {comms.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No communications logged yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {comms.map(c => (
                <div key={c.id} style={{ padding: '0.75rem', background: 'var(--bg-light)', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize' }}>
                      {c.type === 'receipt'       ? '🧾 Rent Receipt' :
                       c.type === 'reminder'      ? '🔔 Rent Reminder' :
                       c.type.startsWith('ic_')   ? `🇮🇪 Compliance: ${c.subject}` :
                       c.subject || c.type}
                    </div>
                    {c.subject && c.type !== 'receipt' && c.type !== 'reminder' && !c.type.startsWith('ic_') && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>{c.subject}</div>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
                    {new Date(c.sent_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Move-out date modal — doubles as both the initial "Move Out"
          action and the "Edit Move-Out Date" correction, since both are
          the same underlying date field on the same tenant. Title and
          button text adjust based on whether the tenant is already
          moved_out, so it reads correctly either way. */}
      {showMoveOutModal && (
        <div className="modal-overlay" onClick={() => !movingOut && setShowMoveOutModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <span className="modal-title">{tenant.status === 'moved_out' ? `✏️ Edit Move-Out Date — ${tenant.first_name} ${tenant.last_name}` : `🚪 Move ${tenant.first_name} ${tenant.last_name} Out`}</span>
              <button type="button" className="modal-close" onClick={() => setShowMoveOutModal(false)} disabled={movingOut}>×</button>
            </div>
            <div className="form-group">
              <label>When did they actually move out?</label>
              <input
                type="date"
                value={moveOutDateInput}
                max={todayISO()}
                onChange={e => setMoveOutDateInput(e.target.value)}
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Defaults to today — change this if they actually left earlier (e.g. you're only recording it now, after the fact). This date is what arrears and rent-owed calculations use going forward.
              </small>
            </div>
            {moveOutError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{moveOutError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowMoveOutModal(false)} disabled={movingOut}>Cancel</button>
              <button type="button" className="btn btn-danger" disabled={movingOut} onClick={handleMoveOut}>
                {movingOut ? 'Saving…' : (tenant.status === 'moved_out' ? 'Save Date' : 'Confirm Move Out')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEraseModal && (
        <div className="modal-overlay" onClick={() => !erasing && setShowEraseModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <span className="modal-title">🔒 Erase Tenant Data (GDPR)</span>
              <button type="button" className="modal-close" onClick={() => setShowEraseModal(false)} disabled={erasing}>×</button>
            </div>
            <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'0.375rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.82rem', color:'#78350f', lineHeight:1.6 }}>
              This permanently removes {tenant.first_name} {tenant.last_name}'s name, email, phone, date of birth,
              and emergency/guarantor contact details. It cannot be undone.
              <br /><br />
              <strong>Kept</strong> — property, room, lease dates, rent amount and full payment history, retained
              for tax record purposes.
            </div>
            {eraseError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{eraseError}</div>}
            <div className="form-group">
              <label>Reason (optional, for your own records)</label>
              <input
                type="text"
                value={eraseReason}
                onChange={e => setEraseReason(e.target.value)}
                placeholder="e.g. Tenant requested erasure via email"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowEraseModal(false)} disabled={erasing}>Cancel</button>
              <button type="button" className="btn btn-danger" disabled={erasing} onClick={handleErase}>
                {erasing ? 'Erasing…' : '🔒 Permanently Erase Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
